import { returnsCenterIdFormat } from "@redotech/redo-model/ids";
import {
  ConsolidationShipToAddress,
  DEFAULT_RETURNS_PROCESSING_COST,
  DEFAULT_RETURNS_PROCESSING_FEE,
  ExternalLocationHubConfig,
  ReturnProcessingLevel,
  ReturnsCenterConfig,
  Team,
} from "@redotech/redo-model/team";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import {
  groupInput,
  input,
  InputProvider,
  numberValidator,
} from "@redotech/ui/form";
import { deepIntersectionEquals } from "@redotech/util/equal";
import { memo, useCallback, useMemo } from "react";
import {
  AvailableReturnsCentersTable,
  ReturnsCenterConfigFormValue,
} from "./available-returns-centers-table";

export const availableReturnsCentersInput = input<
  ReturnsCenterConfigFormValue[]
>({ equal: deepIntersectionEquals });

export const redoManagedReturnsSettingsForm = groupInput({
  enabled: input<boolean>(),
  processingFee: input<string>({ validator: numberValidator({ min: 0 }) }),
  processingCost: input<string>({ validator: numberValidator({ min: 0 }) }),
  coverageShare: input<string>({ validator: numberValidator({ min: 0 }) }),
  returnProcessingLevel: input<ReturnProcessingLevel>(),
  externalLocationHubMappings: input<
    Record<string, { provider: string; locationId: string }>
  >({ equal: deepIntersectionEquals }),
  availableReturnsCenters: availableReturnsCentersInput,
});

export type RedoManagedReturnsSettingsForm = InputProvider.Form<
  typeof redoManagedReturnsSettingsForm
>;
export type RedoManagedReturnsSettingsValue = InputProvider.Value<
  typeof redoManagedReturnsSettingsForm
>;

export const redoManagedReturnsSettingsDefault: RedoManagedReturnsSettingsValue =
  {
    enabled: false,
    processingFee: `${DEFAULT_RETURNS_PROCESSING_FEE}`,
    processingCost: `${DEFAULT_RETURNS_PROCESSING_COST}`,
    coverageShare: "",
    returnProcessingLevel: ReturnProcessingLevel.VERIFICATION,
    externalLocationHubMappings: {},
    availableReturnsCenters: [],
  };

export const redoManagedReturnsTeamToFormValue = (
  team: Team,
): RedoManagedReturnsSettingsValue => {
  const storedMappings =
    team.settings.redoManagedReturns?.externalLocationHubMappings;
  const formMappings: Record<string, { provider: string; locationId: string }> =
    {};
  if (storedMappings) {
    for (const [key, value] of Object.entries(storedMappings)) {
      // DB stores raw numeric keys; convert to formatted for form display
      const formattedKey =
        key && !returnsCenterIdFormat.test(key)
          ? returnsCenterIdFormat.write(BigInt(key))
          : key;
      formMappings[formattedKey] = {
        provider: value.provider,
        locationId: value.locationId ?? "",
      };
    }
  }

  const storedCenters =
    team.settings.redoManagedReturns?.availableReturnsCenters ?? [];
  const formCenters: ReturnsCenterConfigFormValue[] = storedCenters.map(
    (config) => {
      const formattedCenterId =
        config.returnsCenterId &&
        !returnsCenterIdFormat.test(config.returnsCenterId)
          ? returnsCenterIdFormat.write(BigInt(config.returnsCenterId))
          : config.returnsCenterId;
      return {
        returnsCenterId: formattedCenterId,
        consolidationScheduleDays:
          config.consolidationScheduleDays?.toString() ?? "",
        shipToName: config.shipToAddress?.name ?? "",
        shipToStreet1: config.shipToAddress?.street1 ?? "",
        shipToStreet2: config.shipToAddress?.street2 ?? "",
        shipToCity: config.shipToAddress?.city ?? "",
        shipToState: config.shipToAddress?.state ?? "",
        shipToZip: config.shipToAddress?.zip ?? "",
        shipToCountry: config.shipToAddress?.country ?? "",
        refulfillment: config.refulfillment ?? false,
      };
    },
  );

  return {
    enabled: team.settings.redoManagedReturns?.enabled ?? false,
    processingFee: (
      team.settings.redoManagedReturns?.processingFee ??
      DEFAULT_RETURNS_PROCESSING_FEE
    ).toString(),
    processingCost: (
      team.settings.redoManagedReturns?.processingCost ??
      DEFAULT_RETURNS_PROCESSING_COST
    ).toString(),
    coverageShare: team.redoManagedReturnsPricePerOrder ?? "",
    returnProcessingLevel:
      team.settings.redoManagedReturns?.returnProcessingLevel ??
      ReturnProcessingLevel.VERIFICATION,
    externalLocationHubMappings: formMappings,
    availableReturnsCenters: formCenters,
  };
};

export const redoManagedReturnsFormValueToTeam = (
  redoManagedReturnsSettings: RedoManagedReturnsSettingsValue,
): Team["settings"]["redoManagedReturns"] => {
  const mappings = redoManagedReturnsSettings.externalLocationHubMappings;
  const hubMappings: Record<string, ExternalLocationHubConfig> = {};
  if (mappings) {
    for (const [key, value] of Object.entries(mappings)) {
      if (value.provider) {
        // Convert formatted key (rctr_...) back to raw numeric for DB storage
        const rawKey = returnsCenterIdFormat.test(key)
          ? returnsCenterIdFormat.read(key).toString()
          : key;
        hubMappings[rawKey] = {
          provider: value.provider as ExternalLocationHubConfig["provider"],
          locationId:
            value.locationId && value.locationId.trim().length > 0
              ? value.locationId
              : undefined,
        };
      }
    }
  }

  const formCenters = redoManagedReturnsSettings.availableReturnsCenters ?? [];
  const availableReturnsCenters: ReturnsCenterConfig[] = formCenters.map(
    (c) => {
      const rawCenterId = returnsCenterIdFormat.test(c.returnsCenterId)
        ? returnsCenterIdFormat.read(c.returnsCenterId).toString()
        : c.returnsCenterId;

      const hasAddress = c.shipToName || c.shipToStreet1;
      const shipToAddress: ConsolidationShipToAddress | undefined = hasAddress
        ? {
            name: c.shipToName,
            street1: c.shipToStreet1,
            street2: c.shipToStreet2 || undefined,
            city: c.shipToCity,
            state: c.shipToState || undefined,
            zip: c.shipToZip,
            country: c.shipToCountry,
          }
        : undefined;

      const days = Number(c.consolidationScheduleDays);

      return {
        returnsCenterId: rawCenterId,
        consolidationScheduleDays:
          c.consolidationScheduleDays === "" || isNaN(days) ? undefined : days,
        shipToAddress,
        refulfillment: c.refulfillment ?? undefined,
      };
    },
  );

  return {
    enabled: redoManagedReturnsSettings.enabled,
    processingFee: isNaN(Number(redoManagedReturnsSettings.processingFee))
      ? DEFAULT_RETURNS_PROCESSING_FEE
      : Number(redoManagedReturnsSettings.processingFee),
    processingCost: isNaN(Number(redoManagedReturnsSettings.processingCost))
      ? DEFAULT_RETURNS_PROCESSING_COST
      : Number(redoManagedReturnsSettings.processingCost),
    returnProcessingLevel:
      redoManagedReturnsSettings.returnProcessingLevel ??
      ReturnProcessingLevel.VERIFICATION,
    externalLocationHubMappings:
      Object.keys(hubMappings).length > 0 ? hubMappings : undefined,
    availableReturnsCenters:
      availableReturnsCenters.length > 0 ? availableReturnsCenters : undefined,
  };
};

const processingLevelOptions: RedoListItem<ReturnProcessingLevel>[] =
  Object.values(ReturnProcessingLevel).map((returnProcessingLevel) => ({
    type: "text" as const,
    id: returnProcessingLevel,
    value: returnProcessingLevel,
    text:
      returnProcessingLevel.charAt(0).toUpperCase() +
      returnProcessingLevel.slice(1),
  }));

export const RedoManagedReturnsSettingsCardV2 = memo(
  function RedoManagedReturnsSettingsCardV2({
    input,
    teamId,
  }: {
    input: RedoManagedReturnsSettingsForm;
    teamId: string;
  }) {
    const selectedProcessingLevel = useMemo(() => {
      return processingLevelOptions.find(
        (option) => option.value === input.inputs.returnProcessingLevel.value,
      );
    }, [input.inputs.returnProcessingLevel.value]);

    const handleProcessingLevelSelected = useCallback(
      ({ item }: { item: RedoListItem<ReturnProcessingLevel> }) => {
        input.inputs.returnProcessingLevel.setValue(item.value);
      },
      [input.inputs.returnProcessingLevel],
    );

    return (
      <>
        <RedoTextInput
          IconTrailing={() => <span>USD</span>}
          label="Processing Fee (per return)"
          min={0}
          placeholder="0.00"
          setValue={input.inputs.processingFee.setValue}
          state={!input.inputs.enabled.value ? "disabled" : "default"}
          step={0.01}
          value={input.inputs.processingFee.value}
        />

        <RedoTextInput
          IconTrailing={() => <span>USD</span>}
          label="3PL Processing Cost (per return)"
          min={0}
          placeholder="0.00"
          setValue={input.inputs.processingCost.setValue}
          state={!input.inputs.enabled.value ? "disabled" : "default"}
          step={0.01}
          value={input.inputs.processingCost.value}
        />

        <RedoTextInput
          description="Amount of coverage price that will be invoiced as Redo Managed Returns coverage (subtracts from the coverage price)"
          IconTrailing={() => <span>USD</span>}
          label="RMR Coverage Share"
          min={0}
          placeholder="0.00 (no rev share)"
          setValue={input.inputs.coverageShare.setValue}
          state={!input.inputs.enabled.value ? "disabled" : "default"}
          step={0.01}
          value={input.inputs.coverageShare.value}
        />

        <RedoSingleSelectDropdownInput
          disabled={!input.inputs.enabled.value}
          label="Return Processing Level"
          options={processingLevelOptions}
          optionSelected={handleProcessingLevelSelected}
          placeholder="Select return processing level..."
          selectedItem={selectedProcessingLevel}
          size="md"
        />

        <Flex dir="column" gap="sm">
          <Text fontSize="sm" fontWeight="semibold">
            Available Returns Centers
          </Text>
          <Text fontSize="xs" textColor="tertiary">
            Select which returns centers this merchant can use. Configure
            consolidation schedule and ship-to address per center.
          </Text>
          <AvailableReturnsCentersTable
            disabled={!input.inputs.enabled.value}
            hubMappings={input.inputs.externalLocationHubMappings.value}
            input={input.inputs.availableReturnsCenters}
            onHubMappingsChange={
              input.inputs.externalLocationHubMappings.setValue
            }
            teamId={teamId}
          />
        </Flex>
      </>
    );
  },
);
